import React, { useEffect, useRef, useState, useMemo } from 'react';
import classNames from 'classnames';
import { ReportPanel } from '../components/ReportViewer';
import { MagicPosterPanel } from './components/ChatMagicPoster';
import { AiGalleryPanel } from './components/AiGallery/AiGalleryPanel';
import { ResizableContainer } from '../components/ResizableContainer';
import { ChatLandingPage } from '../ChatPage';
import useChatHistory from '../ChatPage/hooks/useChatHistory';
import { useAutoLayout } from './hooks';
import { useSearchParamsAppoint, useUrlQueryParam } from '@/pages/aigc/hooks/useQuery';
import { store } from '../ChatPage/hooks/useLocalState';
import {
  INITIAL_QA_CONTAINER_WIDTH,
  MAX_QA_CONTAINER_WIDTH,
  MIN_QA_CONTAINER_WIDTH,
  TEMPLATE_INITIAL_WIDTH_MAP,
  TEMPLATE_MIN_WIDTH_MAP,
  TEMPLATE_INPUT_BOX_OFFSET_MAP,
  DEFAULT_INPUT_BOX_OFFSET,
  ChatTemplate
} from './constants';
import { ChatContentType, TChartTemplate } from './type';
import { getCosReportPanelKey } from './utils';
import { COPILOT_REPORT_SESSION_ID_KEY, COPILOT_REPORT_WINDOW_STATE_KEY } from '../config/constant';
import ChatSmartTable from '../ChatSmartTable';
import './index.scss';

export { CHAT_DS_TEMPLATES } from './constants';

/** 右侧面板组件映射 */
const CHAT_CONTENT_PANEL_MAP = {
  [ChatContentType.Document]: ReportPanel,
  [ChatContentType.SmartTable]: ChatSmartTable,
  [ChatContentType.MagicPoster]: MagicPosterPanel,
  [ChatContentType.AiGallery]: AiGalleryPanel,
};

export const ChatDSTemplate = () => {
  const { sessionId, template } = useSearchParamsAppoint() as { sessionId?: string; template?: TChartTemplate; };
  const [, setTemplateParam] = useUrlQueryParam(['template']);
  const {
    handleUpdateDsReport,
    dsReport,
    startNewConversation,
    conversations,
    streamingState,
  } = useChatHistory();

  // Reset panel when switching conversations
  // 关键: ChatDSTemplate 的 key 是 sessionId, 切换会话时组件会重新挂载
  // 首次挂载跳过: 卡片点击会先置 show=true 再切入 splitView, 不能在 mount 时立刻关掉
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    if (prevSessionIdRef.current === sessionId) {
      return;
    }
    prevSessionIdRef.current = sessionId;

    // 如果是从 Copilot 跳转过来的，不要关闭报告窗口，保持用户在 Copilot 中的操作状态
    const fromCopilot = store.getItem('__dba_from_copilot__', false);
    if (fromCopilot) {
      return;
    }
    // 切换会话后，如果报告是打开状态，则关闭它
    if (dsReport?.show) {
      handleUpdateDsReport({ show: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  // 是否左右布局展开
  const isExpanded = dsReport?.show;
  const [isShowMenu, setIsShowMenu] = useState(false);
  const [initialWidth, setInitialWidth] = useState<{ width: number; } | null>(null);
  const [inputBoxHeight, setInputBoxHeight] = useState<{ height: number; } | null>(null);

  useEffect(() => {
    if (!isExpanded) {
      return undefined;
    }

    const updateSizes = (): void => {
      const chatPageEl = document.querySelector<HTMLElement>(
        '.dba-chat-ds-container .dba-chat-page',
      );
      const inputBoxEl = document.querySelector<HTMLElement>(
        '.dba-chat-ds-container .dba-input-box',
      );
      if (chatPageEl) {
        setInitialWidth({ width: chatPageEl.offsetWidth });
      }
      if (inputBoxEl) {
        setInputBoxHeight({ height: inputBoxEl.offsetHeight });
      }
    };

    updateSizes();

    const chatPageEl = document.querySelector('.dba-chat-ds-container .dba-chat-page');
    const inputBoxEl = document.querySelector('.dba-chat-ds-container .dba-input-box');
    const observer = new MutationObserver(updateSizes);
    if (chatPageEl) {
      observer.observe(chatPageEl, { attributes: true, childList: true, subtree: true });
    }
    if (inputBoxEl) {
      observer.observe(inputBoxEl, { attributes: true, childList: true, subtree: true });
    }

    return () => observer.disconnect();
  }, [isExpanded, sessionId]);

  useEffect(() => {
    // __dba_from_copilot__: CopilotHeader 跳转 Chat 时设为 true，保留报告；否则重置
    if (store.getItem('__dba_from_copilot__', false)) {
      store.setItem('__dba_from_copilot__', false);
      // CopilotHeader 展开时会清空 COPILOT_REPORT_SESSION_ID_KEY，这里重新写入
      // 保证后续用户再次"返回 Copilot"时 reportSessionId 有值，报告能正常恢复
      if (sessionId) {
        store.setItem(COPILOT_REPORT_SESSION_ID_KEY, sessionId);
      }
    } else if (template !== 'splitView' && template !== 'aiExcel' && template !== 'magicPoster') {
      // splitView/aiExcel/magicPoster 是从普通对话临时切入的，dsReport.show 已被外部置为 true，不能重置
      handleUpdateDsReport({ show: false });
    }

    return () => {
      // __dba_to_copilot__: HeaderMenu "返回 Copilot" 时设为 true，保留报告；否则清除
      if (store.getItem('__dba_to_copilot__', false)) {
        store.setItem('__dba_to_copilot__', false);
      } else {
        store.setItem(COPILOT_REPORT_SESSION_ID_KEY, '');
        store.setItem(COPILOT_REPORT_WINDOW_STATE_KEY, null);
      }
      // 通用清理：如果是临时分栏模式（splitView），卸载时移除 URL 中的 template 参数
      const currentTemplate = new URLSearchParams(window.location.search).get('template');
      if (currentTemplate === 'splitView') {
        setTemplateParam({ template: undefined });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // TODO: 修正容器位置左侧菜单收起的时候，顶部遮盖问题，待提供更优方案
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const menuExists = !!document.querySelector('.dba-menu-small');
      setIsShowMenu(menuExists);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, []);

  // 自动打开布局的钩子函数
  useAutoLayout({
    conversations,
    streamingState,
    dsReport,
    onOpenDsReport: handleUpdateDsReport,
  });

  // 当前面板组件类型
  const contentType = (dsReport?.componentType || ChatContentType.Document) as ChatContentType;

  // 智能表格面板只读条件：
  // 1. 智能问答模式（template=splitView 临时切入）下只读预览，参照分享页 readonly 参数
  // 2. 正文 <cos> 标签来源的 xlsx（isCosTag）一律只读预览，与真正表格（aiExcel 模板）可编辑区分
  const isSmartTableReadonly = template === ChatTemplate.SplitView || (dsReport as any)?.isCosTag;

  // 根据 type 获取对应的面板组件，默认为 Document 类型
  // readonly 为各面板公共属性（ChatDSTemplateActionsListProps 已声明），故对组件类型做宽松断言以支持公共 props 直接穿透
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const PanelComponent = useMemo(() => {
    return (CHAT_CONTENT_PANEL_MAP[contentType] || CHAT_CONTENT_PANEL_MAP[ChatContentType.Document]) as React.ComponentType<any>;
  }, [contentType]);
  // 面板组件的公共 props
  const panelProps = useMemo(() => ({
    handleUpdateDsReport,
    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    startNewConversation,
    conversations,
    dsReport,
    sessionId,
    streamingState,
    className: "dba-chat-ds-report-page-right",
    // 智能表格面板：智能问答模式（splitView 临时切入）下只读预览，参照分享页 readonly 参数；其他面板传 undefined 不受影响
    readonly: contentType === ChatContentType.SmartTable ? isSmartTableReadonly : undefined,
  }), [handleUpdateDsReport, startNewConversation, conversations, dsReport, sessionId, streamingState, contentType, isSmartTableReadonly]);

  // 正文 <cos> 卡片切换时重新挂载面板，确保点击哪张就请求哪张的数据
  const panelKey = useMemo(() => getCosReportPanelKey(dsReport), [dsReport]);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const LayoutContainer = (
    isExpanded ? 'div' : React.Fragment
  ) as React.ElementType;
  
  const containerProps = useMemo(() => isExpanded
    ? {
      className: classNames({
        'dba-chat-ds-report-page': isExpanded,
        'dba-chat-ds-container': isExpanded,
        // FIXME: : 以下2个样式需要删除，暂时搞不定...
        'dba-chat-ds-isShowMenu': isShowMenu,
        'dba-chat-ds-isMinWidth': initialWidth?.width && initialWidth.width < 700
      }),
      key: `${sessionId}`,
      style: {
        '--dba-ds-input-box-height': `${(inputBoxHeight?.height || 0) + (TEMPLATE_INPUT_BOX_OFFSET_MAP[dsReport?.componentType as string] ?? DEFAULT_INPUT_BOX_OFFSET)}px`,
        '--sticky-width': initialWidth?.width ? `${initialWidth.width}px` : '100%'
      },
    }
    : {}, [isExpanded, isShowMenu, initialWidth?.width, sessionId, inputBoxHeight?.height, dsReport?.componentType]);

  return (
    // 外层容器
    <LayoutContainer {...containerProps}>
      {/* 左侧QA拖拽容器 */}
      <ResizableContainer
      // 使用常量定义容器宽度
        minWidth={TEMPLATE_MIN_WIDTH_MAP[template as string] || MIN_QA_CONTAINER_WIDTH}
        maxWidth={MAX_QA_CONTAINER_WIDTH}
        initialWidth={TEMPLATE_INITIAL_WIDTH_MAP[template as string] || INITIAL_QA_CONTAINER_WIDTH}
        isShowMask={false}
        isShowResize={isExpanded as boolean}
      >
        {/* 左侧QA问答页 */}
        <ChatLandingPage/>
      </ResizableContainer>
      {/* 右侧面板区域 - 根据 type 动态渲染 */}
      {isExpanded && <PanelComponent key={panelKey} {...panelProps} />}
    </LayoutContainer>
  );
};
